import { Clock3 } from "lucide-react";
import type { DayAvailability, Period } from "../../core/types/provider-registration";
import { AvailabilitySwitch } from "../AvailabilitySwitch";

const formatTimeByRegex = (rawValue: string): string => {
	const normalizedRaw = rawValue.trim();
	const colonMatch = /^(\d{1,2})(?::(\d{0,2}))?$/.exec(normalizedRaw);
	if (colonMatch) {
		const hourPart = colonMatch[1];
		const hour = Number(hourPart);
		if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
			return hourPart[0] ?? "";
		}

		const minuteRaw = (colonMatch[2] ?? "").replace(/\D/g, "").slice(0, 2);
		if (!minuteRaw) return hourPart;
		if (minuteRaw.length === 1) return `${hourPart}:${minuteRaw}`;

		const minute = Number(minuteRaw);
		if (!Number.isFinite(minute) || minute > 59) {
			return `${hourPart}:${minuteRaw[0] ?? ""}`;
		}

		return `${hourPart}:${minuteRaw}`;
	}

	const digits = rawValue.replace(/\D/g, "").slice(0, 4);
	if (!digits) return "";

	if (digits.length === 1) {
		return digits;
	}

	if (digits.length === 2) {
		const hour = Number(digits);
		if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
			return digits[0] ?? "";
		}
		return digits;
	}

	const hourPart = digits.slice(0, 2);
	const hour = Number(hourPart);
	if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
		return digits[0] ?? "";
	}

	if (digits.length === 3) {
		return `${hourPart}:${digits.slice(2)}`;
	}

	const minutePart = digits.slice(2, 4);
	const minute = Number(minutePart);
	if (!Number.isFinite(minute) || minute > 59) {
		return `${hourPart}:${minutePart[0] ?? ""}`;
	}

	return `${hourPart}:${minutePart}`;
};

const normalizeTo12Hour = (rawTime: string) => {
	const masked = formatTimeByRegex(rawTime);
	if (!masked) return "";

	if (!masked.includes(":")) {
		const parsedHours = Number(masked);
		if (!Number.isFinite(parsedHours) || parsedHours < 1 || parsedHours > 12) {
			return masked;
		}
		return `${String(parsedHours).padStart(2, "0")}:00`;
	}

	const [hoursRaw = "", minutesRaw = ""] = masked.split(":");
	const parsedHours = Number(hoursRaw);
	const parsedMinutes = Number(minutesRaw);
	if (!Number.isFinite(parsedHours) || !Number.isFinite(parsedMinutes)) return masked;
	const normalizedHours = ((parsedHours % 12) + 12) % 12 || 12;
	const safeMinutes = Math.min(Math.max(parsedMinutes, 0), 59);
	return `${String(normalizedHours).padStart(2, "0")}:${String(safeMinutes).padStart(2, "0")}`;
};

type ScheduleField = "startTime" | "endTime" | "startPeriod" | "endPeriod";

type AvailabilityScheduleEditorProps = {
	availability: DayAvailability[];
	onToggleDay: (day: DayAvailability["day"]) => void;
	onChangeSchedule: (
		day: DayAvailability["day"],
		field: ScheduleField,
		value: string,
	) => void;
	gridClassName?: string;
};

export function AvailabilityScheduleEditor({
	availability,
	onToggleDay,
	onChangeSchedule,
	gridClassName = "grid gap-4 lg:grid-cols-2",
}: AvailabilityScheduleEditorProps) {
	return (
		<div className={gridClassName}>
			{availability.map((slot) => (
				<div
					key={slot.day}
					className={
						"rounded-xl border px-5 py-4 " +
						(slot.enabled
							? "border-primary bg-white"
							: "border-slate-200 bg-white")
					}
				>
					<div className='flex items-start justify-between gap-3'>
						<div className='text-sm font-semibold text-slate-800'>
							{slot.day}
						</div>

						<AvailabilitySwitch
							checked={slot.enabled}
							onToggle={() => onToggleDay(slot.day)}
							ariaLabel={`${slot.day} disponible`}
						/>
					</div>

					{slot.enabled ? (
						<div className='mt-3 flex items-center gap-2'>
							<Clock3 className='h-4 w-4 shrink-0 text-slate-400' />
							<div className='flex flex-wrap items-center gap-2'>
								<div className='flex items-center gap-2'>
									<input
										type='text'
										aria-label={`Hora de inicio ${slot.day}`}
										title={`Hora de inicio ${slot.day}`}
										inputMode='numeric'
										maxLength={5}
										value={formatTimeByRegex(slot.startTime)}
										onChange={(e) =>
											onChangeSchedule(
												slot.day,
												"startTime",
												formatTimeByRegex(e.target.value),
											)
										}
										onBlur={() =>
											onChangeSchedule(
												slot.day,
												"startTime",
												normalizeTo12Hour(slot.startTime),
											)
										}
										className='h-9 w-29 min-w-29 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700'
										placeholder='hh:mm'
									/>
									<select
										aria-label={`Periodo de inicio ${slot.day}`}
										title={`Periodo de inicio ${slot.day}`}
										value={slot.startPeriod}
										onChange={(e) =>
											onChangeSchedule(
												slot.day,
												"startPeriod",
												e.target.value as Period,
											)
										}
										className='select-with-arrow h-9 w-[5.8rem] rounded-lg border border-slate-200 bg-white pl-2 pr-8 text-sm text-slate-700'
									>
										<option value='AM'>AM</option>
										<option value='PM'>PM</option>
									</select>
								</div>

								<span className='text-sm text-slate-400'>-</span>

								<div className='flex items-center gap-2'>
									<input
										type='text'
										aria-label={`Hora de fin ${slot.day}`}
										title={`Hora de fin ${slot.day}`}
										inputMode='numeric'
										maxLength={5}
										value={formatTimeByRegex(slot.endTime)}
										onChange={(e) =>
											onChangeSchedule(
												slot.day,
												"endTime",
												formatTimeByRegex(e.target.value),
											)
										}
										onBlur={() =>
											onChangeSchedule(
												slot.day,
												"endTime",
												normalizeTo12Hour(slot.endTime),
											)
										}
										className='h-9 w-29 min-w-29 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700'
										placeholder='hh:mm'
									/>
									<select
										aria-label={`Periodo de fin ${slot.day}`}
										title={`Periodo de fin ${slot.day}`}
										value={slot.endPeriod}
										onChange={(e) =>
											onChangeSchedule(
												slot.day,
												"endPeriod",
												e.target.value as Period,
											)
										}
										className='select-with-arrow h-9 w-[5.8rem] rounded-lg border border-slate-200 bg-white pl-2 pr-8 text-sm text-slate-700'
									>
										<option value='AM'>AM</option>
										<option value='PM'>PM</option>
									</select>
								</div>
							</div>
						</div>
					) : (
						<div className='mt-3 flex h-9 items-center justify-center text-sm font-semibold text-slate-400'>
							No disponible
						</div>
					)}
				</div>
			))}
		</div>
	);
}
